import { useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Field,
  TextInput,
  TextArea,
  Select,
} from "@odingard/design-system";
import { EmptyState, ErrorState, LoadingState } from "@odingard/design-system";
import { ApiError } from "@odingard/sdk-client";
import { KNOWLEDGE_ASSET_TYPES } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { proposalApi } from "../../lib/api";
import { DemoModeBanner, LiveModeBadge } from "../../components/ProposalGrounded";

// Knowledge library: create and approve reusable content. Only approved assets
// can ground AI drafts, so approval is an explicit, human-gated action here.
export function PaiKnowledge() {
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(async () => proposalApi.knowledgeAssets(orgId), [orgId]);
  const assets = state.data?.data.assets ?? [];

  const [assetType, setAssetType] = useState<string>("technical_content");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function create() {
    if (!title.trim() || !body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await proposalApi.createKnowledgeAsset(orgId, { assetType, title: title.trim(), body: body.trim() });
      setTitle("");
      setBody("");
      state.reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "Could not create asset.");
    } finally {
      setBusy(false);
    }
  }

  async function approve(id: string) {
    await proposalApi.approveKnowledgeAsset(orgId, id);
    state.reload();
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Knowledge Library</h1>
          <p>Reusable, approved past performance, resumes, and content blocks that ground AI drafting.</p>
        </div>
        <LiveModeBadge demoMode={state.data?.demoMode ?? false} />
      </div>

      <DemoModeBanner show={state.data?.demoMode ?? false} />

      <Card>
        <CardHeader title="Add knowledge asset" />
        <CardBody>
          <div className="ods-stack">
            <Field label="Type">
              {(id) => (
                <Select id={id} value={assetType} onChange={(e) => setAssetType(e.target.value)}>
                  {KNOWLEDGE_ASSET_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Title">
              {(id) => <TextInput id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
            </Field>
            <Field label="Body" help="Approved source content the AI may ground drafts on.">
              {(id) => <TextArea id={id} rows={5} value={body} onChange={(e) => setBody(e.target.value)} />}
            </Field>
            {err && <p style={{ color: "var(--ods-color-danger)", margin: 0 }}>{err}</p>}
            <div>
              <Button size="sm" onClick={create} disabled={busy || !title.trim() || !body.trim()}>
                {busy ? "Saving…" : "Add asset"}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      {state.loading && <LoadingState label="Loading knowledge library…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {!state.loading && assets.length === 0 && (
        <EmptyState title="No knowledge assets" description="Add reusable content above, then approve it for grounding." />
      )}
      {assets.length > 0 && (
        <Card>
          <CardHeader title="Assets" />
          <CardBody>
            <div className="ods-stack">
              {assets.map((a) => (
                <div
                  key={a.id}
                  className="ods-row"
                  style={{ justifyContent: "space-between", padding: "10px 0", borderBottom: "1px solid var(--ods-color-line)" }}
                >
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.title}</div>
                    <span style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)" }}>{a.assetType}</span>
                  </div>
                  <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                    <Badge tone={a.approvalState === "approved" ? "success" : "warning"}>{a.approvalState}</Badge>
                    {a.approvalState !== "approved" && (
                      <Button size="sm" variant="secondary" onClick={() => approve(a.id)}>
                        Approve
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
}
